/**
 * @fullvsl/dashboard/dashboard — analytics dashboard renderer (FUL-17).
 *
 * Renders the MVP analytics surfaces (PRD §10 + §13) from an `AnalyticsDataset`:
 *   - KPI row: impressions, plays, play rate, avg watch %, completion,
 *     CTA exibida, CTA clicada, conversion proxy (cta_click_rate).
 *   - Curva de retenção (SVG) with drop-off + CTA-moment annotations.
 *   - CTA funnel (conversion-decision view: impressions→plays→shown→clicked).
 *   - Comportamento de retomada (offered / accepted / acceptance rate).
 *   - Device / source / aspect-ratio breakdowns (PRD §10.3).
 *   - Raw event log (PRD §13 "Events" view) of recent conversion signals.
 *
 * Self-contained, utilitarian, workflow-focused, responsive (desktop + mobile).
 * No framework dependency — matches the player-prototype slice convention.
 */
import type { AnalyticsDataset } from "./analytics-client";
import type { AnalyticsDataSource } from "./analytics-client";
import {
  breakdownBadge,
  breakdownLabel,
  ctaFunnel,
  conversionSignalCount,
  eventTone,
  fmtClock,
  fmtDate,
  fmtDuration,
  fmtInt,
  fmtPct,
  rankBreakdown,
  recentEvents,
  retentionDisplay,
  resumeSummary,
} from "./analytics-format";
import { FIXTURE_VSL } from "./analytics-fixture";

export interface DashboardOptions {
  source: AnalyticsDataSource;
  /** VSL id to load (defaults to the fixture VSL). */
  vslId?: string;
  /** VSL display name for the header. */
  vslName?: string;
}

/** Idempotently mount + render the dashboard into a host element. */
export async function createDashboard(
  host: HTMLElement,
  opts: DashboardOptions,
): Promise<void> {
  const vslId = opts.vslId ?? FIXTURE_VSL.id;
  host.innerHTML = skeleton();

  let dataset: AnalyticsDataset;
  try {
    dataset = await opts.source.load(vslId);
  } catch (err) {
    host.innerHTML = errorState(err);
    return;
  }
  host.innerHTML = "";
  host.appendChild(renderShell(dataset, opts.vslName ?? FIXTURE_VSL.name));
}

/* -----------------------------------------------------------------------------------
 * Shell layout
 * ----------------------------------------------------------------------------------- */

function renderShell(d: AnalyticsDataset, vslName: string): HTMLElement {
  const root = el("div", "fv-dash");
  root.appendChild(renderHeader(d, vslName));
  root.appendChild(renderKpis(d));
  const grid = el("div", "fv-grid");
  grid.appendChild(renderRetention(d));
  grid.appendChild(renderBreakdownColumn(d));
  root.appendChild(grid);
  root.appendChild(renderFunnelAndResume(d));
  root.appendChild(renderEventLog(d));
  return root;
}

/* -----------------------------------------------------------------------------------
 * Header
 * ----------------------------------------------------------------------------------- */

function renderHeader(d: AnalyticsDataset, vslName: string): HTMLElement {
  const header = el("header", "fv-header");
  const titleWrap = el("div");
  titleWrap.appendChild(el("h1", "fv-title", vslName));
  const sub = el(
    "p",
    "fv-sub",
    `Visão geral das estatísticas · ${fmtDate(d.overview.windowStart)} – ${fmtDate(
      d.overview.windowEnd,
    )} · ${fmtInt(d.events.length)} eventos`,
  );
  titleWrap.appendChild(sub);
  const badge = el(
    "span",
    d.source === "live" ? "fv-badge fv-badge-live" : "fv-badge fv-badge-fixture",
    d.source === "live" ? "● API ativa" : "○ Dados de demonstração",
  );
  titleWrap.appendChild(badge);
  header.appendChild(titleWrap);
  header.appendChild(renderLegend());
  return header;
}

function renderLegend(): HTMLElement {
  const legend = el("div", "fv-legend");
  legend.innerHTML =
    `<span class="fv-key"><i class="fv-dot fv-dot-primary"></i>Retenção</span>` +
    `<span class="fv-key"><i class="fv-dot fv-dot-cta"></i>Momento da CTA</span>`;
  return legend;
}

/* -----------------------------------------------------------------------------------
 * KPI row
 * ----------------------------------------------------------------------------------- */

function renderKpis(d: AnalyticsDataset): HTMLElement {
  const o = d.overview;
  const avgWatchSec = (o.avgWatchPct / 100) * FIXTURE_VSL.durationSec;
  const cards: { label: string; value: string; accent?: boolean; hint?: string }[] = [
    { label: "Impressões", value: fmtInt(o.impressions), hint: "Carregamentos do player" },
    { label: "Reproduções", value: fmtInt(o.plays), hint: "Clique para reproduzir" },
    {
      label: "Taxa de reprodução",
      value: fmtPct(o.playRate),
      accent: true,
      hint: "reproduções / impressões",
    },
    {
      label: "Tempo médio assistido",
      value: fmtDuration(avgWatchSec),
      hint: fmtPct(o.avgWatchPct / 100, 0) + " do vídeo",
    },
    {
      label: "Concluíram",
      value: fmtInt(countType(d, "completed")),
      hint: fmtPct(completionRate(d), 0) + " de conclusão",
    },
    { label: "CTA exibida", value: fmtInt(o.ctaShown), hint: "Chegaram a 50%" },
    {
      label: "CTA clicada",
      value: fmtInt(o.ctaClicked),
      hint: "Sinal de conversão",
    },
    {
      label: "Conversão estimada",
      value: fmtPct(o.ctaClickRate),
      accent: true,
      hint: "cliques / exibições",
    },
  ];
  const row = el("div", "fv-kpis");
  for (const c of cards) {
    const card = el("div", "fv-kpi" + (c.accent ? " fv-kpi-accent" : ""));
    card.appendChild(el("p", "fv-kpi-label", c.label));
    const valWrap = el("p", "fv-kpi-value");
    valWrap.textContent = c.value;
    card.appendChild(valWrap);
    if (c.hint) card.appendChild(el("p", "fv-kpi-hint", c.hint));
    row.appendChild(card);
  }
  return row;
}

function countType(d: AnalyticsDataset, type: string): number {
  let n = 0;
  for (const e of d.events) if (e.type === type) n += 1;
  return n;
}

function completionRate(d: AnalyticsDataset): number {
  const o = d.overview;
  return o.impressions > 0 ? countType(d, "completed") / o.impressions : 0;
}

/* -----------------------------------------------------------------------------------
 * Curva de retenção (SVG)
 * ----------------------------------------------------------------------------------- */

function renderRetention(d: AnalyticsDataset): HTMLElement {
  const card = el("section", "fv-card fv-card-wide");
  card.appendChild(el("h2", "fv-card-title", "Curva de retenção"));
  card.appendChild(
    el("p", "fv-card-sub", "Percentual de espectadores que alcançou cada faixa de progresso assistido."),
  );

  const rd = retentionDisplay(d.overview, 50);
  const svgHolder = el("div", "fv-chart");
  svgHolder.appendChild(renderRetentionSvg(rd));
  card.appendChild(svgHolder);

  if (rd.biggestDrop) {
    card.appendChild(
      note(
        `Maior queda entre ${rd.biggestDrop.fromPct}% e ${rd.biggestDrop.toPct}%: ${fmtInt(
          rd.biggestDrop.lost,
        )} espectadores saíram.`,
        "warn",
      ),
    );
  }
  card.appendChild(
    note(
      `${fmtInt(rd.viewersAtCta)} espectadores chegaram à CTA de 50% · ${fmtInt(
        rd.droppedBeforeCta,
      )} saíram antes dela.`,
      "info",
    ),
  );
  return card;
}

function renderRetentionSvg(rd: ReturnType<typeof retentionDisplay>): SVGElement {
  const W = 720;
  const H = 260;
  const padL = 40;
  const padR = 16;
  const padT = 16;
  const padB = 28;
  const ns = "http://www.w3.org/2000/svg";
  const svg = document.createElementNS(ns, "svg");
  svg.setAttribute("viewBox", `0 0 ${W} ${H}`);
  svg.setAttribute("preserveAspectRatio", "none");
  svg.classList.add("fv-retention");

  const pts = rd.points;
  const maxX = 100;
  const maxY = pts.length ? Math.max(...pts.map((p) => p.retained), 1) : 1;
  const x = (pct: number) => padL + (pct / maxX) * (W - padL - padR);
  const y = (retained: number) => padT + (1 - retained / maxY) * (H - padT - padB);

  // gridlines (0/25/50/75/100).
  for (const g of [0, 25, 50, 75, 100]) {
    const lx = x(g);
    const line = document.createElementNS(ns, "line");
    line.setAttribute("x1", String(lx));
    line.setAttribute("x2", String(lx));
    line.setAttribute("y1", String(padT));
    line.setAttribute("y2", String(H - padB));
    line.setAttribute("class", "fv-grid");
    svg.appendChild(line);
    const t = document.createElementNS(ns, "text");
    t.setAttribute("x", String(lx));
    t.setAttribute("y", String(H - padB + 16));
    t.setAttribute("class", "fv-axis");
    t.textContent = `${g}%`;
    svg.appendChild(t);
  }

  if (pts.length) {
    const linePath = pts
      .map((p, i) => `${i === 0 ? "M" : "L"} ${x(p.pct).toFixed(1)} ${y(p.retained).toFixed(1)}`)
      .join(" ");
    const line = document.createElementNS(ns, "path");
    line.setAttribute("d", linePath);
    line.setAttribute("class", "fv-ret-line");
    svg.appendChild(line);

    const area = document.createElementNS(ns, "path");
    area.setAttribute(
      "d",
      `${linePath} L ${x(pts[pts.length - 1].pct).toFixed(1)} ${H - padB} L ${x(
        pts[0].pct,
      ).toFixed(1)} ${H - padB} Z`,
    );
    area.setAttribute("class", "fv-ret-area");
    svg.appendChild(area);

    // CTA moment marker at 50%.
    const cx = x(50);
    const ctaLine = document.createElementNS(ns, "line");
    ctaLine.setAttribute("x1", String(cx));
    ctaLine.setAttribute("x2", String(cx));
    ctaLine.setAttribute("y1", String(padT));
    ctaLine.setAttribute("y2", String(H - padB));
    ctaLine.setAttribute("class", "fv-cta-line");
    svg.appendChild(ctaLine);
    const ctaLabel = document.createElementNS(ns, "text");
    ctaLabel.setAttribute("x", String(cx + 4));
    ctaLabel.setAttribute("y", String(padT + 12));
    ctaLabel.setAttribute("class", "fv-cta-label");
    ctaLabel.textContent = "CTA 50%";
    svg.appendChild(ctaLabel);

    // biggest-drop annotation dot.
    if (rd.biggestDrop) {
      const dropPt = pts.find((p) => p.pct === rd.biggestDrop!.toPct) ?? pts[pts.length - 1];
      const dot = document.createElementNS(ns, "circle");
      dot.setAttribute("cx", String(x(dropPt.pct)));
      dot.setAttribute("cy", String(y(dropPt.retained)));
      dot.setAttribute("r", "4");
      dot.setAttribute("class", "fv-drop-dot");
      svg.appendChild(dot);
    }
  } else {
    const t = document.createElementNS(ns, "text");
    t.setAttribute("x", String(W / 2));
    t.setAttribute("y", String(H / 2));
    t.setAttribute("text-anchor", "middle");
    t.setAttribute("class", "fv-empty");
    t.textContent = "Ainda não há dados de retenção";
    svg.appendChild(t);
  }
  return svg;
}

/* -----------------------------------------------------------------------------------
 * Breakdowns (device / source / aspect)
 * ----------------------------------------------------------------------------------- */

function renderBreakdownColumn(d: AnalyticsDataset): HTMLElement {
  const col = el("div", "fv-bd-col");
  col.appendChild(
    renderBreakdownCard("Dispositivos", "device", d.breakdown.byDevice),
  );
  col.appendChild(
    renderBreakdownCard("Principais origens", "source", d.breakdown.bySource),
  );
  col.appendChild(
    renderBreakdownCard("Proporção do vídeo", "aspect", d.breakdown.byAspectRatio),
  );
  return col;
}

function renderBreakdownCard(
  title: string,
  dim: "device" | "source" | "aspect",
  tally: Record<string, number>,
): HTMLElement {
  const card = el("section", "fv-card");
  card.appendChild(el("h2", "fv-card-title", title));
  const rows = rankBreakdown(tally);
  if (rows.length === 0) {
    card.appendChild(note("Ainda não há dados para esta dimensão.", "info"));
    return card;
  }
  const list = el("div", "fv-bd-list");
  for (const r of rows) {
    const row = el("div", "fv-bd-row");
    const head = el("div", "fv-bd-head");
    const left = el("div", "fv-bd-left");
    if (dim !== "aspect") {
      left.appendChild(el("span", "fv-bd-badge", breakdownBadge(dim, r.key)));
    }
    left.appendChild(el("span", "fv-bd-name", breakdownLabel(dim, r.key)));
    head.appendChild(left);
    head.appendChild(
      el("span", "fv-bd-meta", `${fmtInt(r.count)} · ${fmtPct(r.share, 0)}`),
    );
    row.appendChild(head);
    const bar = el("div", "fv-bar");
    const fill = el("div", "fv-bar-fill");
    fill.style.width = `${Math.max(2, r.share * 100).toFixed(1)}%`;
    bar.appendChild(fill);
    row.appendChild(bar);
    list.appendChild(row);
  }
  card.appendChild(list);
  return card;
}

/* -----------------------------------------------------------------------------------
 * CTA funnel + resume
 * ----------------------------------------------------------------------------------- */

function renderFunnelAndResume(d: AnalyticsDataset): HTMLElement {
  const wrap = el("div", "fv-grid fv-grid-two");

  // Funnel.
  const funnelCard = el("section", "fv-card");
  funnelCard.appendChild(el("h2", "fv-card-title", "Funil de conversão"));
  funnelCard.appendChild(
    el("p", "fv-card-sub", "Da impressão ao clique na CTA, usando a métrica de conversão do MVP."),
  );
  const steps = ctaFunnel(d.overview);
  const fList = el("div", "fv-funnel");
  for (const s of steps) {
    const item = el("div", "fv-funnel-step");
    const top = el("div", "fv-funnel-top");
    top.appendChild(el("span", "fv-funnel-label", s.label));
    const meta = el("span", "fv-funnel-meta");
    meta.textContent =
      s.stepRate == null ? `${fmtPct(s.ofImpressions, 0)} das impressões` : `${fmtPct(
        s.ofImpressions,
        0,
      )} das impressões · ${fmtPct(s.stepRate)} da etapa`;
    top.appendChild(meta);
    item.appendChild(top);
    item.appendChild(el("div", "fv-funnel-count", fmtInt(s.count)));
    const bar = el("div", "fv-bar");
    const fill = el("div", "fv-bar-fill");
    fill.style.width = `${Math.max(3, s.ofImpressions * 100).toFixed(1)}%`;
    bar.appendChild(fill);
    item.appendChild(bar);
    fList.appendChild(item);
  }
  funnelCard.appendChild(fList);
  wrap.appendChild(funnelCard);

  // Resume.
  const resumeCard = el("section", "fv-card");
  resumeCard.appendChild(el("h2", "fv-card-title", "Comportamento de retomada"));
  resumeCard.appendChild(
    el("p", "fv-card-sub", "Visitantes recorrentes que receberam a opção de retomar o vídeo."),
  );
  const rs = resumeSummary(d.overview);
  const rGrid = el("div", "fv-resume-grid");
  rGrid.appendChild(statBlock("Perguntas exibidas", fmtInt(rs.offered)));
  rGrid.appendChild(statBlock("Aceitaram", fmtInt(rs.accepted)));
  rGrid.appendChild(statBlock("Taxa de aceitação", fmtPct(rs.acceptanceRate), true));
  resumeCard.appendChild(rGrid);
  if (rs.offered === 0) {
    resumeCard.appendChild(note("Ainda não há visitantes recorrentes neste período.", "info"));
  }
  wrap.appendChild(resumeCard);
  return wrap;
}

function statBlock(label: string, value: string, accent = false): HTMLElement {
  const b = el("div", "fv-stat" + (accent ? " fv-stat-accent" : ""));
  b.appendChild(el("p", "fv-stat-label", label));
  b.appendChild(el("p", "fv-stat-value", value));
  return b;
}

/* -----------------------------------------------------------------------------------
 * Registro de eventos
 * ----------------------------------------------------------------------------------- */

function renderEventLog(d: AnalyticsDataset): HTMLElement {
  const card = el("section", "fv-card fv-card-wide");
  const head = el("div", "fv-ev-head");
  head.appendChild(el("h2", "fv-card-title", "Registro de eventos"));
  head.appendChild(
    el(
      "p",
      "fv-card-sub",
      `${fmtInt(conversionSignalCount(d.events))} sinais de conversão neste período.`,
    ),
  );
  card.appendChild(head);

  const rows = recentEvents(d.events, 12);
  const tableWrap = el("div", "fv-table-wrap");
  if (rows.length === 0) {
    tableWrap.appendChild(note("Ainda não há eventos.", "info"));
    card.appendChild(tableWrap);
    return card;
  }
  const table = el("table", "fv-table");
  table.innerHTML =
    `<thead><tr><th>Horário</th><th>Evento</th><th>Origem</th><th>Dispositivo</th><th>Posição</th></tr></thead>`;
  const tbody = el("tbody");
  for (const e of rows) {
    const tr = el("tr");
    tr.appendChild(el("td", "fv-mono", fmtClock(e.clientTs)));
    tr.appendChild(
      el("td", `fv-ev-tag fv-ev-${eventTone(e.type)}`, e.type),
    );
    tr.appendChild(el("td", "", (e.meta?.source as string) ?? "—"));
    tr.appendChild(el("td", "", (e.meta?.device as string) ?? "—"));
    tr.appendChild(
      el(
        "td",
        "fv-mono",
        e.meta?.positionSec != null ? `${e.meta.positionSec}s` : "—",
      ),
    );
    tbody.appendChild(tr);
  }
  table.appendChild(tbody);
  tableWrap.appendChild(table);
  card.appendChild(tableWrap);
  return card;
}

/* -----------------------------------------------------------------------------------
 * Loading / error / small helpers
 * ----------------------------------------------------------------------------------- */

function skeleton(): string {
  return `<div class="fv-dash"><p class="fv-loading">Carregando estatísticas...</p></div>`;
}

function errorState(err: unknown): string {
  const msg = err instanceof Error ? err.message : String(err);
  return (
    `<div class="fv-dash">` +
    `<section class="fv-card fv-card-wide"><h2 class="fv-card-title">Não foi possível carregar as estatísticas</h2>` +
    `<p class="fv-error">${escapeHtml(msg)}</p>` +
    `<p class="fv-card-sub">Verifique a fonte dos dados e a disponibilidade do serviço de estatísticas.</p>` +
    `</section></div>`
  );
}

function note(text: string, tone: "info" | "warn"): HTMLElement {
  return el("p", `fv-note fv-note-${tone}`, text);
}

function el(tag: string, cls = "", text?: string): HTMLElement {
  const node = document.createElement(tag);
  if (cls) node.className = cls;
  if (text != null) node.textContent = text;
  return node;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => {
    switch (c) {
      case "&":
        return "&amp;";
      case "<":
        return "&lt;";
      case ">":
        return "&gt;";
      case '"':
        return "&quot;";
      default:
        return "&#39;";
    }
  });
}
